import React, { useCallback } from 'react'
import { isEmpty } from 'lodash'
import { StreamData } from 'types/streamData'
import { WidgetId } from 'features/types'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { SocialWebStreamWidget } from 'schemas/SocialWebStreamWidgetSchema'
import { AudienceStreamWidget } from 'schemas/AudienceStreamWidgetSchema'
import {
  PodcastSoundbiteFrame,
  SoundbiteNoHeaderFrame
} from 'components/visualization/SoundbiteFrame'
import LinkedDomainIcon from 'components/visualization/SoundbiteFrame/DomainIcon/LinkedDomainIcon'
import PodcastContent from 'components/visualization/SoundbiteFrame/PostContent/PodcastContent'
import HeaderInfo from 'components/visualization/SoundbiteFrame/HeaderInfo'
import StyledAvatar from 'components/visualization/SoundbiteFrame/StyledAvatar'
import LinkedDate from 'components/visualization/SoundbiteFrame/LinkedDate'
import PhotoUserTagContent from 'components/visualization/SoundbiteFrame/PhotoUserTagContent'
import { useMedia } from 'components/visualization/SoundbiteFrame/Media'
import { MediaClickHandler } from 'components/visualization/SoundbiteFrame/Media/MediaGrid'
import ActionIcons from 'components/visualization/SoundbiteFrame/ActionIcons'
import MetadataIcons from 'components/visualization/SoundbiteFrame/MetadataIcons'
import { useAppDispatch } from 'app/hooks'
import { openLightbox } from 'features/ui/uiSlice'

export const PodcastSoundbite = ({
  widgetId,
  streamData,
  serialNumber
}: {
  widgetId: WidgetId
  streamData: StreamData
  serialNumber: number
}): React.ReactElement => {
  const { defaultMediaDisplayOption, highlighting } = useDashboardWidgetSetting<
    SocialWebStreamWidget | AudienceStreamWidget
  >(widgetId)

  const dispatch = useAppDispatch()
  const mediaClickHandler: MediaClickHandler = useCallback(
    (mediaUrls, index) => {
      dispatch(openLightbox({ slides: mediaUrls, defaultIndex: index }))
    },
    [dispatch]
  )

  const { mediaGrid, mediaController } = useMedia(streamData, {
    defaultDisplayOption: defaultMediaDisplayOption,
    onMediaClick: mediaClickHandler
  })

  const { title, authorName, authorHandle, isMediaDoc, photoUserTags } = streamData

  if (!title && !authorName && !authorHandle) {
    return (
      <SoundbiteNoHeaderFrame
        avatar={<StyledAvatar streamData={streamData} />}
        dateItem={<LinkedDate streamData={streamData} />}
        domainIcon={<LinkedDomainIcon streamData={streamData} />}
        postContent={<PodcastContent streamData={streamData} highlighting={highlighting} />}
        actionItems={
          <>
            {isMediaDoc && mediaController}
            <ActionIcons streamData={streamData} />
          </>
        }
        metadataItems={<MetadataIcons streamData={streamData} />}
        mediaItems={isMediaDoc && mediaGrid}
        hasWarningInfo={false}
        photoUserTagContent={
          !isEmpty(photoUserTags) && <PhotoUserTagContent streamData={streamData} />
        }
        serialNumber={serialNumber}
      />
    )
  }

  return (
    <PodcastSoundbiteFrame
      avatar={<StyledAvatar streamData={streamData} />}
      dateItem={<LinkedDate streamData={streamData} />}
      domainIcon={<LinkedDomainIcon streamData={streamData} />}
      headerInfo={<HeaderInfo streamData={streamData} />}
      postContent={<PodcastContent streamData={streamData} highlighting={highlighting} />}
      actionItems={
        <>
          {isMediaDoc && mediaController}
          <ActionIcons streamData={streamData} />
        </>
      }
      metadataItems={<MetadataIcons streamData={streamData} />}
      mediaItems={isMediaDoc && mediaGrid}
      hasWarningInfo={false}
      photoUserTagContent={
        !isEmpty(photoUserTags) && <PhotoUserTagContent streamData={streamData} />
      }
      serialNumber={serialNumber}
    />
  )
}

export default PodcastSoundbite
